import React from 'react';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { PROFILE_FORM_EDITED_TEXT_SX } from '../profileFormStyles';

/**
 * Wrapper component for form fields that handles:
 * - Grid layout (field + optional edited indicator)
 * - "Edited" text display for Edit mode
 * - Optional badge rendered alongside the field (e.g. Skillko-critical field indicator)
 * 
 * @param {React.ReactNode} children - The form field component
 * @param {boolean} showEdited - Whether to show the "(Edited)" indicator
 * @param {boolean} isEdited - Whether the field value has been edited
 * @param {number} fieldWidth - Grid width for the field (default 6 for edit mode, 8 for others)
 * @param {number} indicatorWidth - Grid width for the indicator column
 * @param {React.ReactNode} badge - Optional badge rendered next to the field (e.g. SkillkoFieldBadge)
 */
const FieldWrapper = ({ 
  children, 
  showEdited = false, 
  isEdited = false,
  fieldWidth = 8,
  indicatorWidth = 4,
  badge = null,
}) => {
  const fieldContent = badge ? (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
      <Box sx={{ mt: 2 }}>{badge}</Box>
    </Box>
  ) : (
    children
  );

  // If not showing edited indicator, use simpler layout
  if (!showEdited) {
    return (
      <>
        <Grid item sm={fieldWidth} xs={12}>
          {fieldContent}
        </Grid>
        <Grid item sm={indicatorWidth} xs={0}></Grid>
      </>
    );
  }

  // Edit mode layout with edited indicator
  return (
    <>
      <Grid item sm={6} xs={9}>
        {fieldContent}
      </Grid>
      <Grid item sm={6} xs={3}>
        {isEdited && <EditedText />}
      </Grid>
    </>
  );
};

/**
 * "(Edited)" text indicator component
 */
const EditedText = () => (
  <Typography component="span" sx={PROFILE_FORM_EDITED_TEXT_SX}>
    (Edited)
  </Typography>
);

export { FieldWrapper, EditedText };
export default FieldWrapper;

